import { FC, useState, useEffect } from "react";
import { IDocumentCreatorProps } from "../../../../@types/documents";
import { IActDisagregationCreateDTO, IActDisagregationGetDTO, IActDisagregationUpdateDTO } from "../../../../libs/coreapi-dto/documents/actDisagregation";
import { IActDisagregationItemEditDTO, IActDisagregationItemGetDTO } from "../../../../libs/coreapi-dto/documents/actDisagregationItem";
import { ActDisagregationDataProvider } from "../../../../Services/DataProviders/ActDisagregationDataProvider";
import { useAppContext } from "../../../../system/providers/appContextProvider";
import { useTabsContext } from "../../../../system/providers/tabsProvider";
import { ActDisagregationUpdateForm } from "./ActDisagregationUpdateForm";
import { v4 as uuidv4 } from "uuid";
import { getActDisagregationItemUpdateDTO } from "../../../../libs/core-api-requests/customRequest/ActDisagregationItemUpdateRequest";
import { DateTime } from "luxon";
import { IEntitySimpleDTO } from "../../../../libs/coreapi-dto/@types/common";
import { useUserContext } from "../../../../system/providers/userContextProvider";
import { IKizConfirmationData } from "../../../../libs/coreapi-dto/dirs/kiz";
import { DocumentOperationSpinner } from "../../../../components/DocumentOperationSpinner/DocumentOperationSpinner";
import { resolve } from "mathjs";
import { reject } from "lodash";

interface ICreateDocument {
    callback: (actDisagregation: IActDisagregationCreateDTO, withClose: boolean, view: GridStateType) => void;
}

interface IEditDocument {
    actDisagregationDocument: IActDisagregationGetDTO;
    documentItemsUpdate: IActDisagregationItemEditDTO[];
    documentUpdate: IActDisagregationUpdateDTO;
    documentCopy?: IActDisagregationGetDTO;
}

const ActDisagregationCreatorView: FC<IDocumentCreatorProps<IActDisagregationCreateDTO>> = (props) => {
    const appCtx = useAppContext();
    const userContext = useUserContext()
    const tabsContext = useTabsContext();

    const actDisagregationDP = new ActDisagregationDataProvider(appCtx.coreApiService);

    const [viewState, setViewState] = useState<GridStateType>(props.variant);
    const [idGlobal, setIdGlobal] = useState<string | undefined>(props.idGlobal);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [createDocument, setCreateDocument] = useState<ICreateDocument | null>(null);
    const [editDocument, setEditDocument] = useState<IEditDocument | null>(null);

    useEffect(() => {
        const initializeComponent = async () => {
            setIsLoading(true)

            if (viewState === "create") {
                try {
                    await new Promise((resolve, reject) => {
                        const createCallback = (actDisagregation: IActDisagregationCreateDTO, withClose: boolean, view: GridStateType) => {
                            const tabToClose = tabsContext.currentTab;
                            setIsSubmitting(true);
                            actDisagregationDP.create(actDisagregation, (e) => {
                                setIdGlobal(e);
                                setViewState(view);
                                props.createdCallback?.();
                                withClose && tabToClose && tabsContext.close(tabToClose);
                                setIsSubmitting(false);
                            }, ()=>{}, (e)=>{
                                setIsSubmitting(false);
                            });
                        }
                        setCreateDocument({ callback: createCallback });
                        resolve(null)
                    })
                } catch(error) {
                    console.error('Error loading document:', error);
                } finally {
                    setIsLoading(false)
                }
            }
            
            else if (viewState === "edit") {
                try {
                    await new Promise((resolve, reject) => {
                        actDisagregationDP.getById(idGlobal as string, (actDisagregationDocument) => {
                            getActDisagregationItemUpdateDTO(actDisagregationDocument.idGlobal as string, actDisagregationDocument.isUnpackRecursive as boolean, (e) => {
                                const itemsUpdate: IActDisagregationItemEditDTO[] = [];
                                e.forEach((item) => {
                                    itemsUpdate.push({
                                        idGlobal: item.idGlobal,
                                        barCode: item.barCode,
                                        goodsCode: item.goodsCode,
                                        producer: item.producer,
                                        denominator: item.denominator,
                                        gtdNumber: item.gtdNumber,
                                        idGoodsGlobal: item.idGoodsGlobal,
                                        idLotFromGlobal: item.idLotFromGlobal,
                                        idScalingRatioFromGlobal: item.idScalingRatioFromGlobal,
                                        idScalingRatioToGlobal: item.idScalingRatioToGlobal,
                                        kizBoxCount: item.kizBoxCount,
                                        kizCount: item.kizCount,
                                        numerator: item.numerator,
                                        producerPrice: item.producerPrice,
                                        productMargin: item.productMargin,
                                        quantity: item.quantity,
                                        quantityFrom: item.quantityFrom,
                                        registerPrice: item.registerPrice,
                                        retailCostInfo: item.retailCostInfo,
                                        retailPriceIncVat: item.retailPriceIncVat,
                                        supplierCostInfo: item.supplierCostInfo,
                                        isGnvls: item.isGnvls,
                                        isKiz: item.isKiz,
                                        isKizBox: item.isKizBox,
                                        kizBoxes: item.kizBoxes,
                                        palettes: item.palettes,
                                        kizs: item.kizs,
                                        kizConfirmationScanned: actDisagregationDocument.trustedSupplier ? [] :
                                            actDisagregationDocument.isUnpackRecursive ? item.kizs?.map(x => {
                                                return {
                                                    idGlobal: x.idGlobal,
                                                    infoScanned: x.isTrusted ? x.gtinSGtin : "",
                                                    infoSupplier: x.gtinSGtin
                                                } as IKizConfirmationData
                                            }) :
                                                item.kizBoxes?.map(x => {
                                                    return {
                                                        idGlobal: x.idGlobal,
                                                        infoScanned: x.isTrusted ? x.sscc : "",
                                                        infoSupplier: x.sscc
                                                    } as IKizConfirmationData
                                                }),
                                        gtin: item.gtin
                                    } as IActDisagregationItemEditDTO);
                                });
            
                                const documentUpdate: IActDisagregationUpdateDTO = {
                                    idGlobal: actDisagregationDocument.idGlobal as string,
                                    documentDate: actDisagregationDocument.documentDate.toF3DateTimeServerSide(userContext),
                                    idStoreGlobal: actDisagregationDocument.store.idGlobal,
                                    comment: actDisagregationDocument.comment,
                                    idUserGlobal: undefined,
                                    idGosContractGlobal: actDisagregationDocument?.gosContract?.idGlobal ?? undefined,
                                    isUnpackRecursive: actDisagregationDocument.isUnpackRecursive,
                                    isNewBarcode: actDisagregationDocument.isNewBarcode,
                                    trustedSupplier: actDisagregationDocument.trustedSupplier,
                                    items: itemsUpdate,
                                    idBaseDocumentGlobal: actDisagregationDocument.baseDocument?.idGlobal
                                };
            
                                setEditDocument({
                                    actDisagregationDocument: actDisagregationDocument,
                                    documentItemsUpdate: itemsUpdate,
                                    documentUpdate: documentUpdate,
                                });
                                resolve(null)
                            });
                        });
                    })
                } catch(error) {
                    console.error('Error loading document:', error);
                } finally {
                    setIsLoading(false)
                }
                
            }
            
            else if (viewState === "copy") {
                try {
                    await new Promise((resolve, reject) => {
                        const itemsUpdate: IActDisagregationItemEditDTO[] = [];
                        
                    const idGlobalNew = uuidv4();
                    const documentUpdate: IActDisagregationUpdateDTO = {
                        idGlobal: idGlobalNew,
                        documentDate: DateTime.now(),
                        items: itemsUpdate,
                        isUnpackRecursive: true,
                        trustedSupplier: false,
                        isNewBarcode: false
                    };
    
                    const documentCopy: IActDisagregationGetDTO = {
                        idGlobal: idGlobalNew,
                        documentDate: DateTime.now(),
                        store: {} as IEntitySimpleDTO,
                        amount: 0,
                        amountVat: 0,
                        isUnpackRecursive: true,
                        trustedSupplier: false,
                        isNewBarcode: false,
                        dateCreated: DateTime.now(),
                        items: [],
                        displayName: "",
                        documentState: "",
                        documentStateModified: DateTime.now(),
                        mnemocode: ""
                    };
    
                    setEditDocument({
                        actDisagregationDocument: documentCopy,
                        documentItemsUpdate: itemsUpdate,
                        documentUpdate: documentUpdate,
                        documentCopy: documentCopy,
                    });
                    resolve(null)
                    })

                } catch(error) {
                    console.error('Error loading document:', error);
                } finally {
                    setIsLoading(false)
                }
            }
        }
        initializeComponent()
        
    }, [viewState]);

    if (isLoading) {
        return (
            <div >
                <DocumentOperationSpinner xl data-testid="invoice-creator-loading" />
            </div>
        );
    }

    return (
        <>
            {viewState === "create" ? (
                createDocument && (
                    <ActDisagregationUpdateForm
                        isSubmitting={isSubmitting}
                        document={null}
                        idGlobal={props.idGlobal}
                        documentItems={null}
                        documentUpdate={null}
                        variant="create"
                        save={(actDisagregation: IActDisagregationCreateDTO) => createDocument.callback(actDisagregation, false, "edit")}
                        ok={(actDisagregation: IActDisagregationCreateDTO) => createDocument.callback(actDisagregation, true, "create")}
                        cancel={() => tabsContext.currentTab && tabsContext.close(tabsContext.currentTab)}
                        permission={props.permission}
                    />
                )
            ) : viewState === "edit" ? (
                editDocument && (
                    <ActDisagregationUpdateForm
                        isSubmitting={isSubmitting}
                        variant="edit"
                        save={(actDisagregation: IActDisagregationUpdateDTO) => {
                            setIsSubmitting(true);
                            actDisagregationDP.update(idGlobal as string, actDisagregation, (e) => {
                                props.createdCallback?.();
                                setIsSubmitting(false);
                            });
                        }}
                        ok={(actDisagregation: IActDisagregationUpdateDTO) => {
                            setIsSubmitting(true);
                            actDisagregationDP.update(idGlobal as string, actDisagregation, (e) => {
                                props.createdCallback?.();
                                tabsContext.currentTab && tabsContext.close(tabsContext.currentTab);
                                setIsSubmitting(false);
                            });
                        }}
                        cancel={() => tabsContext.currentTab && tabsContext.close(tabsContext.currentTab)}
                        document={editDocument.actDisagregationDocument}
                        documentItems={editDocument.actDisagregationDocument.items}
                        documentUpdate={editDocument.documentUpdate}
                        permission={props.permission}
                    />
                )
            ) : viewState === "copy" ? (
                editDocument && (
                    <ActDisagregationUpdateForm
                        isSubmitting={isSubmitting}
                        variant="copy"
                        save={(actDisagregation: IActDisagregationUpdateDTO) => {
                            setIsSubmitting(true);
                            actDisagregationDP.create(actDisagregation, (e) => {
                                setIdGlobal(e);
                                props.createdCallback?.();
                                setIsSubmitting(false);
                            }, ()=>{}, (e)=>{
                                setIsSubmitting(false);
                            });
                        }}
                        ok={(actDisagregation: IActDisagregationUpdateDTO) => {
                            setIsSubmitting(true);
                            actDisagregationDP.create(actDisagregation, (e) => {
                                setIdGlobal(e);
                                props.createdCallback?.();
                                tabsContext.currentTab && tabsContext.close(tabsContext.currentTab);
                                setIsSubmitting(false);
                            }, ()=>{}, (e)=>{
                                setIsSubmitting(false);
                            });
                        }}
                        cancel={() => tabsContext.currentTab && tabsContext.close(tabsContext.currentTab)}
                        document={editDocument.documentCopy as IActDisagregationGetDTO}
                        documentItems={editDocument.documentCopy?.items as IActDisagregationItemGetDTO[]}
                        documentUpdate={editDocument.documentUpdate}
                        permission={props.permission}
                    />
                )
            ) : (
                <></>
            )}
        </>
    );
};

export default ActDisagregationCreatorView;
